/**
 * StepperInCanvas — Etap 3 T2 (2026-05-17).
 *
 * Renderuje stepper / wizard w canvasie kreatora. Wszystkie kroki rozwinięte
 * jeden pod drugim (decyzja UX 2026-05-17: easier drag&drop pól między krokami
 * niż top-bar + tylko aktywny krok). Runtime renderer (`RecursiveFormRenderer.StepperRenderer`)
 * pokazuje 1 aktywny krok naraz — to inny widok niż canvasie.
 *
 * Model danych: `LayoutElement.steps: Step[]` gdzie `Step = { id, title, icon?, validation?, children }`.
 *
 * Edytor wspólny dla `type='stepper'` i `type='wizard'` (różnica tylko w runtime renderer,
 * decyzja UX 2026-05-17). Model identyczny.
 *
 * Każdy krok ma:
 *  - DragHandle 6-kropek (drag step → reorder w obrębie tego samego steppera)
 *  - Numer porządkowy (1, 2, 3...)
 *  - IconPicker — ikona kroku (renderowana w runtime przed numerem)
 *  - Inline edit tytułu (pencil → input → Enter zapisuje / Escape anuluje)
 *  - Trash button (ConfirmDialog gdy children > 0, od razu gdy pusty)
 *  - Content (zawsze widoczny w canvasie) — drop target dla pól/elementów z palety
 *
 * Wzorzec analogiczny do `AccordionInCanvas` (E3.T1 + pakiet B Sub-2).
 */

import {
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, Pencil, Plus, Trash2, X as XIcon } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { LayoutElement, Step } from '@/lib/layout-tree';

import { DragHandle } from './DragHandle';
import { IconPicker, renderIconByName } from './IconPicker';

export interface StepperInCanvasProps {
  element: LayoutElement;
  /** Renderuje pojedyncze dziecko kroku — parent dispatch'uje (field / nested element). */
  renderChild: (child: LayoutElement, containerId: string) => ReactNode;
  /** Dodaje nowy Step do steppera. */
  onAddStep: () => void;
  /** Aktualizuje tytuł istniejącego kroku. */
  onUpdateStepTitle: (stepId: string, newTitle: string) => void;
  /** Aktualizuje ikonę istniejącego kroku. */
  onUpdateStepIcon: (stepId: string, newIcon: string) => void;
  /** Usuwa krok (z dziećmi). Parent zarządza dalszą akcją po confirm. */
  onRemoveStep: (stepId: string) => void;
}

export function StepperInCanvas({
  element,
  renderChild,
  onAddStep,
  onUpdateStepTitle,
  onUpdateStepIcon,
  onRemoveStep,
}: StepperInCanvasProps): ReactNode {
  const { t } = useTranslation();
  const steps = element.steps ?? [];
  const [pendingDelete, setPendingDelete] = useState<Step | null>(null);

  const handleRemoveClick = (step: Step) => {
    if (step.children.length === 0) {
      onRemoveStep(step.id);
    } else {
      setPendingDelete(step);
    }
  };

  return (
    <div data-testid={`stepper-canvas-${element.id}`}>
      <div className="space-y-2 rounded-lg border border-border-primary p-3">
        <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {steps.map((step, idx) => (
            <StepperStepInCanvas
              key={step.id}
              step={step}
              stepperId={element.id}
              stepNumber={idx + 1}
              onCommitTitle={(newTitle) => onUpdateStepTitle(step.id, newTitle)}
              onCommitIcon={(newIcon) => onUpdateStepIcon(step.id, newIcon)}
              onRemove={() => handleRemoveClick(step)}
              renderChild={renderChild}
            />
          ))}
        </SortableContext>
      </div>
      <button
        type="button"
        onClick={onAddStep}
        className="mt-2 inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/20"
        data-testid={`stepper-canvas-${element.id}-add-step`}
      >
        <Plus className="h-3 w-3" />
        {t('tenant.systemModuleBuilder.stepper.addStep')}
      </button>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.systemModuleBuilder.stepper.confirmDeleteTitle')}
        message={
          pendingDelete
            ? t(
                pendingDelete.children.length === 1
                  ? 'tenant.systemModuleBuilder.stepper.confirmDeleteMessageOne'
                  : 'tenant.systemModuleBuilder.stepper.confirmDeleteMessageOther',
                { title: pendingDelete.title, count: pendingDelete.children.length },
              )
            : ''
        }
        variant="danger"
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) onRemoveStep(pendingDelete.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

interface StepperStepInCanvasProps {
  step: Step;
  stepperId: string;
  stepNumber: number;
  onCommitTitle: (newTitle: string) => void;
  onCommitIcon: (newIcon: string) => void;
  onRemove: () => void;
  renderChild: (child: LayoutElement, containerId: string) => ReactNode;
}

function StepperStepInCanvas({
  step,
  stepperId,
  stepNumber,
  onCommitTitle,
  onCommitIcon,
  onRemove,
  renderChild,
}: StepperStepInCanvasProps): ReactNode {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(step.title);

  // Step jest sortowalny w obrębie tego samego steppera.
  // `source: 'stepper-step'` rozpoznawane przez `usePaletteDnD.onDragEnd` (ścieżka 5).
  const sortable = useSortable({
    id: step.id,
    data: { source: 'stepper-step', stepperId, stepId: step.id },
  });
  const sortableStyle: CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : undefined,
  };
  const handleProps: HTMLAttributes<HTMLDivElement> = {
    ...sortable.attributes,
    ...sortable.listeners,
  };

  // Content kroku jest drop targetem dla pól/elementów z palety (parytet z panelem akordeonu).
  // `target='section'` z `sectionId=step.id` — usePaletteDnD rozpoznaje przez kontrakt
  // generic-container (sekcja LUB panel LUB krok).
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `${step.id}-drop`,
    data: { source: 'section', target: 'section', sectionId: step.id },
  });

  const startEdit = () => {
    setDraft(step.title);
    setEditing(true);
  };

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed.length > 0 && trimmed !== step.title) {
      onCommitTitle(trimmed);
    }
    setEditing(false);
  };

  const cancel = () => {
    setDraft(step.title);
    setEditing(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  return (
    <div
      ref={sortable.setNodeRef}
      style={sortableStyle}
      className="rounded border border-border-secondary bg-bg-primary"
      data-testid={`stepper-canvas-step-${step.id}`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <DragHandle
          ariaLabel={t('tenant.systemModuleBuilder.stepper.dragStep', { title: step.title })}
          dragHandleProps={handleProps}
          className="p-0.5"
        />
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-100 text-xs font-semibold text-accent-700 dark:bg-accent-900/40 dark:text-accent-300">
          {stepNumber}
        </span>
        <IconPicker
          value={step.icon ?? 'Circle'}
          onChange={onCommitIcon}
          ariaLabel={t('tenant.systemModuleBuilder.stepper.iconPicker', { title: step.title })}
        />
        {editing ? (
          <>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              aria-label={t('tenant.systemModuleBuilder.stepper.titleInput')}
              className="flex-1 rounded border border-border-primary bg-bg-primary px-2 py-1 text-sm text-text-primary"
              data-testid={`stepper-canvas-step-${step.id}-title-input`}
            />
            <button
              type="button"
              onClick={commit}
              aria-label={t('tenant.systemModuleBuilder.stepper.saveTitle')}
              className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={cancel}
              aria-label={t('tenant.systemModuleBuilder.stepper.cancelEdit')}
              className="rounded p-1 text-text-muted hover:bg-bg-tertiary"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <span className="flex-1 text-sm font-medium text-text-primary">{step.title}</span>
            <button
              type="button"
              onClick={startEdit}
              aria-label={t('tenant.systemModuleBuilder.stepper.editStepTitle', {
                title: step.title,
              })}
              className="rounded p-1 text-text-muted opacity-60 hover:bg-bg-tertiary hover:text-accent-600 hover:opacity-100"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label={t('tenant.systemModuleBuilder.stepper.removeStep', { title: step.title })}
              className="rounded p-1 text-text-muted opacity-60 hover:bg-red-50 hover:text-red-600 hover:opacity-100 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>

      <div
        ref={setDropRef}
        className={`border-t border-border-secondary p-3 ${
          isOver ? 'bg-accent-50/50 ring-2 ring-dashed ring-accent-400 dark:bg-accent-900/20' : ''
        }`}
        data-testid={`stepper-canvas-step-${step.id}-drop`}
      >
        {step.children.length > 0 ? (
          <div className="space-y-2">
            <SortableContext
              items={step.children.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {step.children.map((child) => (
                <div key={child.id}>{renderChild(child, step.id)}</div>
              ))}
            </SortableContext>
          </div>
        ) : (
          <p className="rounded border border-dashed border-border-primary px-3 py-4 text-center text-xs text-text-tertiary">
            {t('tenant.systemModuleBuilder.stepper.emptyStep')}
          </p>
        )}
      </div>

      {/* renderIconByName helper — runtime renderer użyje go w top-bar steppera */}
      <span className="hidden" aria-hidden="true">
        {renderIconByName(step.icon)}
      </span>
    </div>
  );
}
